import {Navigate, useLocation, useNavigate} from "react-router-dom";
import React, {useCallback, useEffect, useState} from "react";
import {PstatCmntModel, PstatModel} from "app/model/info/pstat";
import moment from "moment";
import {Constant} from "app/constants/constant";
import ArcDownload, {dataSourceProps} from "app/components/arc/ArcDownload";
import {FileInfoModel} from "app/model/common/file";
import {useAppDispatch, useAppSelector} from "app/store/hook";
import {showAndHideSpinner} from "app/reducers/spinnerSlice";
import {FileService} from "app/services/common/file";
import ArcButton, {ButtonColor, ButtonType} from "app/components/arc/ArcButton";
import {PstatService} from "app/services/info/pstatService";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import {ResponseResultCode} from "app/model/response-api";
import {ArcEditorUtil} from "app/components/arc/ArcEditor";
import {UserInfoUtil} from "app/utils/userInfoUtil";
import {AuthConstant} from "app/constants/authConstant";
import {BltnbClCd, PrstsCd} from "app/model/common/code";
import ArcEditorView from "app/components/arc/ArcEditorView";
import {openModal} from "app/reducers/modalSlice";
import AppAlert from "app/components/common/AppAlert";
import ArcTitle from "../../../../components/arc/ArcTitle";
import { CoService } from "app/services/common/CoService";
import { logCd, logMsg } from "app/model/common/actvLogModel";
import { FirebaseService } from "app/services/common/FirebaseService";

export default function Info004View() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const userInfo = useAppSelector(state => state.userInfo.userInfo);

  if (!location.state) {
    return <Navigate to="/invalid-access" replace />;
  }

  const initialData = () => {
    if (location.state) {
      try {
        return JSON.parse(location.state);
      } catch {
        return location.state;
      }
    } else {
      return new PstatModel(BltnbClCd.API);
    }
  }

  const initialCmnt = () => {
    if (location.state) {
      let data = initialData();
      if (data.cmntList && data.cmntList.length > 0) {
        return data.cmntList[0];
      }
    }
    return undefined;
  }

  const [data, setData] = useState<PstatModel>(initialData);
  const [files, setFiles] = useState<dataSourceProps[]>([]);
  const [cmnt, setCmnt] = useState<PstatCmntModel | undefined>(initialCmnt);
  const [cmntFiles, setCmntFiles] = useState<dataSourceProps[]>([]);
  const [showCmnt, setShowCmnt] = useState<boolean>(false);
  const [status, setStatus] = useState<string>();
  const [statusClass, setStatusClass] = useState<string>();

  const modifyButton = (
      <ArcButton type={ButtonType.modify}
                 hasIcon={true}
                 className={"mgr5"}
                 onClick={handleClickModify}/>);
  const deleteButton = (
      <ArcButton type={ButtonType.delete}
                 hasIcon={true}
                 className={"mgr5"}
                 onClick={() => confirmDelete(handleClickDelete)}/>);
  const addCmntButton = (
      <ArcButton type={ButtonType.custom}
                 text="답변 등록"
                 color={ButtonColor.btn6}
                 className={"mgr5"}
                 onClick={handleClickAddCmnt}/>);
  const modifyCmntButton = (
      <ArcButton type={ButtonType.modify}
                 hasIcon={true}
                 text="답변 수정"
                 className={"mgr5"}
                 onClick={handleClickCmntModify}/>);
  const deleteCmntButton = (
      <ArcButton type={ButtonType.delete}
                 hasIcon={true}
                 text="답변 삭제"
                 className={"mgr5"}
                 onClick={() => confirmDelete(handleClickCmntDelete)}/>);
  const completeCmntButton = (
      <ArcButton type={ButtonType.save}
                 text="답변 완료"
                 className={"mgr5"}
                 onClick={handleClickCmntComplete}/>);

  useEffect(() => {
    setData(initialData);
    setCmnt(initialCmnt);
  }, [location.state]);

  useEffect(() => {
    const files = data.file?.files ?? [];
    setFiles(getDownloadFiles(files));

    if (PrstsCd.PROCESS === data.prstsCd) {
      if (data.cmntList && data.cmntList.length > 0) {
        setStatus(t('info.code.prstsCd.PROGRESS').toString());
        setStatusClass('p-badge2');
      } else {
        setStatus(t(`info.code.prstsCd.${data.prstsCd}`).toString());
        setStatusClass('p-badge5');
      }
    } else {
      setStatus(t(`info.code.prstsCd.${data.prstsCd}`).toString());
      setStatusClass('p-badge1');
    }
  }, [data]);

  useEffect(() => {
    if (cmnt) {
      const files = cmnt.file?.files ?? [];
      setCmntFiles(getDownloadFiles(files));
    } else {
      setCmntFiles([]);
    }
  }, [cmnt]);

  useEffect(() => {
    if (UserInfoUtil.hasRole(userInfo, AuthConstant.ADMIN)) {
      setShowCmnt(true);
    } else {
      if (PrstsCd.COMPLETE === data.prstsCd) {
        setShowCmnt(true);
      } else {
        setShowCmnt(false);
      }
    }
  }, [userInfo, data.prstsCd]);

  function confirmDelete(deleteFunc: () => void) {
    dispatch(openModal({
      path: '/info/003/delete/confirm',
      target: AppAlert,
      confirmProps: {
        type: 'delete',
        onConfirm: () => deleteFunc(),
      }
    }));
  }

  function getDownloadFiles(files: FileInfoModel[]): dataSourceProps[] {
    if (files.length > 0) {
      return files.map((f: FileInfoModel): dataSourceProps => {
        return {realName: f.realNm ?? "", saveName: f.fileNm ?? ""};
      });
    } else {
      return [];
    }
  }

  const userButtons = useCallback(() => {
    if (UserInfoUtil.hasRole(userInfo, AuthConstant.ADMIN)) {
      const component = [deleteButton];

      if (PrstsCd.PROCESS === data.prstsCd) {
        if (!cmnt) {
          component.push(addCmntButton);
        } else {
          component.push(modifyCmntButton, deleteCmntButton, completeCmntButton);
        }
      }

      return <>{component.map((btn, index) => <span key={index}>{btn}</span>)}</>;
    } else {
      if (!cmnt && userInfo.userId === data.regrId) {
        return <>{modifyButton}{deleteButton}</>;
      }
    }
  }, [userInfo, cmnt, data.prstsCd, data.regrId]);

  async function handleClickDownload(selectFile: dataSourceProps) {
    const dataFile = data.file?.files ?? [];
    await doDownloadFile(dataFile, selectFile);
  }

  async function handleClickCmntDownload(selectFile: dataSourceProps) {
    const dataFile = cmnt?.file?.files ?? [];
    await doDownloadFile(dataFile, selectFile);
  }

  async function doDownloadFile(files: FileInfoModel[], selectFile: dataSourceProps) {
    if (files && files.length > 0) {
      const selectedFile = files.find(f => f.fileNm === selectFile.saveName);
      if (selectedFile) {
        dispatch(showAndHideSpinner(true));
        await FileService.getInstance().downloadFile(process.env.REACT_APP_URL_WP ?? '', selectedFile);
        dispatch(showAndHideSpinner(false));
      }
    }

  }

  function handleClickGoList() {
    navigate("/info/api/info004");
  }

  function handleClickModify() {
    navigate("/info/api/info004/form", {state: data});
  }

  async function handleClickDelete() {
    if (data.pstatId) {
      dispatch(showAndHideSpinner(true));
      await PstatService.getInstance().deleteById(data.pstatId)
      .then(response => {
        dispatch(showAndHideSpinner(false));
        if (response && ResponseResultCode.Y === response.resultCode) {
          deleteImageFromContent(data.pstatCtt ?? "");
          CoService.getInstance().saveUserActvLog({
            actvLogCd: logCd.IO,
            logMsgCtt: logMsg.IO.apiR.D,
          });
          toast.success(t('common.message.success.delete', {title: ''}));
          navigate("/info/api/info004", {replace: true});
        } else {
          toast.error(t('common.message.error.delete', {title: ''}));
        }
      })
      .catch(() => {
        dispatch(showAndHideSpinner(false));
        toast.error(t('common.message.error.delete', {title: ''}));
      });
    } else {
      toast.error(t('common.message.error.idNotExist', {title: ''}));
    }
  }

  async function deleteImageFromContent(content: string) {
    if (content) {
      const images = ArcEditorUtil.getImageFromContent(content);
      await FileService.getInstance().deleteAtomFile(process.env.REACT_APP_URL_WP ?? '', images);
    }
  }

  function handleClickAddCmnt() {
    navigate("/info/api/info004/write-ask", {state: data});
  }

  function handleClickCmntModify() {
    navigate("/info/api/info004/write-ask", {state: data});
  }

  async function handleClickCmntDelete() {
    if (cmnt?.cmntId) {
      dispatch(showAndHideSpinner(true));
      await PstatService.getInstance().deleteCmntById(cmnt.cmntId)
      .then(response => {
        dispatch(showAndHideSpinner(false));
        if (response && ResponseResultCode.Y === response.resultCode) {
          deleteImageFromContent(cmnt.cmntCtt ?? "");
          toast.success(t('common.message.success.delete', {title: ''}));
          const noCmnt = {...data, cmntList: []};
          navigate(location.pathname, {state: noCmnt, replace: true});
        } else {
          toast.error(t('common.message.error.delete', {title: ''}));
        }
      })
      .catch(() => {
        dispatch(showAndHideSpinner(false));
        toast.error(t('common.message.error.system'));
      });
    } else {
      toast.error(t('common.message.error.idNotExist', {title: ''}));
    }
  }

  async function handleClickCmntComplete() {
    if (data.pstatId && cmnt) {
      dispatch(openModal({
        path: '/info/api/info004/complete/confirm',
        target: AppAlert,
        confirmProps: {
          type: 'confirm',
          title: t('confirm.label.message.statusChange'),
          message: t('confirm.label.message.statusChangeConfirm'),
          onConfirm: fetchSetStatusToComplete,
        }
      }));
    }
  }

  async function fetchSetStatusToComplete() {
    const param: PstatModel = {...data, prstsCd: PrstsCd.COMPLETE};
    await PstatService.getInstance().update(param)
    .then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        const alarmRequest = {
          uid: data.regrId,
          typeId: 'API_2',
          parameter: {
            title: `${data.titlNm}`
          },
          data: {
            _link: window.location.origin + window.location.pathname + '#/info/api/info004',
          }
        }
        FirebaseService.getInstance().sendMessage(alarmRequest);
        toast.success(t('common.message.success.statusChange'));
        navigate(location.pathname, {state: response.data, replace: true});
      } else {
        toast.error(t('common.message.error.statusChange'));
      }
    })
    .catch(() => toast.error(t('common.message.error.system')));
  }

  return (
    <div id="container">
      <div className="sub-wrap">
        <ArcTitle title="API 요청 게시판" />
        <div className="col-wrap">
          <div className="col-md-12">
            <div className="cont">
              <div className="tbl-st-row">
                <table>
                  <caption>API 상세</caption>
                  <colgroup>
                    <col style={{ width: '100%' }} />
                  </colgroup>
                  <tbody className="al">
                  <tr>
                    <td className="pdt20 pbd10">
                      <h3 className="titT1" style={{whiteSpace: 'break-spaces'}}>
                        {data.titlNm}
                      </h3>
                      <ul className="posting-status">
                        <li>{data.regrNm}</li>
                        <li>
                          {data.regDate ? moment(data.regDate).format(Constant.FORMAT_YMD) : ''}
                        </li>
                        <li>
                          <em className={statusClass}>{status ?? ''}</em>
                        </li>
                      </ul>
                    </td>
                  </tr>
                  <tr>
                    <td className="pdt20 pdb20">
                      <ArcEditorView value={data.pstatCtt ?? ""}/>
                    </td>
                  </tr>
                  {/*<tr>*/}
                  {/*  <td className="pdt10 pdb10">*/}
                  {/*    <ArcDownload*/}
                  {/*      dataSource={files}*/}
                  {/*      onDownload={handleClickDownload}*/}
                  {/*    />*/}
                  {/*  </td>*/}
                  {/*</tr>*/}
                  {(showCmnt && cmnt) && (
                    <>
                      <tr>
                        <td className="pdt10 pdb10">
                          <ul className="posting-status">
                            <li>관리자</li>
                            <li>
                              {(PrstsCd.COMPLETE === data.prstsCd && data.chgDate)
                                ? moment(data.chgDate).format(Constant.FORMAT_YMD)
                                : ''
                              }
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td className="pdt20 pdb20">
                          <ArcEditorView value={cmnt.cmntCtt ?? ""}/>
                        </td>
                      </tr>
                      {/*<tr>*/}
                      {/*  <td className="pdt10 pdb10">*/}
                      {/*    <ArcDownload*/}
                      {/*      dataSource={cmntFiles}*/}
                      {/*      onDownload={handleClickCmntDownload}*/}
                      {/*    />*/}
                      {/*  </td>*/}
                      {/*</tr>*/}
                    </>
                  )}
                  </tbody>
                </table>
              </div>

              <div className="btns ar">
                <>{userButtons()}</>
                <ArcButton type={ButtonType.list}
                           className={"mg0"}
                           onClick={handleClickGoList}/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
